import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';

import {
  Box,
  FCC,
  HStack,
  LoadingIcon,
  Paragraph,
  cn,
} from '@eluve/components';
import {
  SUMMARY_TAB,
  TRANSCRIPT_TAB,
  useAppointmentActivity,
  useAppointmentTabNav,
} from '@eluve/frontend-appointment-hooks';

const Tab: FCC<{
  onClick: () => void;
  active: boolean;
  compact: boolean;
  disabled?: boolean;
}> = ({ onClick, active, children, disabled, compact }) => (
  <Box zStack className="flex-1">
    {active && (
      <motion.div
        className={cn(
          'absolute inset-0 z-0 border border-border-primary bg-bg-primary',
          compact ? 'rounded' : 'rounded-md',
        )}
        layout="position"
        layoutId="active-tab"
      />
    )}
    <button
      onClick={onClick}
      className={cn(
        'z-10',
        compact ? 'p-1.5' : 'p-2',
        disabled && 'cursor-not-allowed',
      )}
      disabled={disabled}
    >
      {children}
    </button>
  </Box>
);

interface AppointmentTabsProps {
  compact?: boolean;
  disabledSummary?: boolean;
}

export const AppointmentTabs: React.FC<AppointmentTabsProps> = ({
  compact = false,
  disabledSummary,
}) => {
  const { setTab, tab } = useAppointmentTabNav();
  const { isSummarizationInProgress, isTranscriptionInProgress } =
    useAppointmentActivity();

  return (
    <AnimatePresence>
      <LayoutGroup>
        <HStack
          className={cn(
            'overflow-hidden border border-border-primary bg-bg-tertiary',
            compact ? 'rounded-md p-px' : 'rounded-lg p-0.5',
          )}
          gap={0}
        >
          <Tab
            active={tab === TRANSCRIPT_TAB}
            compact={compact}
            onClick={() => setTab(TRANSCRIPT_TAB)}
          >
            <HStack justify="center">
              {isTranscriptionInProgress && <LoadingIcon size="xs" />}
              <Paragraph
                className="transition-colors duration-200"
                color={tab === TRANSCRIPT_TAB ? 'primary' : 'supporting'}
                size={compact ? 'sm' : 'lg'}
                weight={compact ? 'semibold' : 'regular'}
              >
                Transcript
              </Paragraph>
            </HStack>
          </Tab>
          <Tab
            active={tab === SUMMARY_TAB}
            compact={compact}
            disabled={disabledSummary}
            onClick={() => setTab(SUMMARY_TAB)}
          >
            <HStack justify="center">
              {isSummarizationInProgress && <LoadingIcon size="xs" />}
              <Paragraph
                className="transition-colors duration-200"
                color={
                  tab === SUMMARY_TAB
                    ? 'primary'
                    : disabledSummary
                      ? 'disabled'
                      : 'supporting'
                }
                size={compact ? 'sm' : 'lg'}
                weight={compact ? 'semibold' : 'regular'}
              >
                Summary
              </Paragraph>
            </HStack>
          </Tab>
        </HStack>
      </LayoutGroup>
    </AnimatePresence>
  );
};

export const TranscriptTabContent: FCC = ({ children }) => {
  const { tab } = useAppointmentTabNav();
  if (tab !== TRANSCRIPT_TAB) return null;
  return (
    <motion.div
      initial={{ x: -60, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 60, opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="flex size-full flex-1 flex-col gap-2"
    >
      {children}
    </motion.div>
  );
};

export const SummaryTabContent: FCC = ({ children }) => {
  const { tab } = useAppointmentTabNav();
  if (tab !== SUMMARY_TAB) return null;
  return (
    <motion.div
      initial={{ x: 60, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -60, opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="flex size-full flex-1 flex-col gap-2"
    >
      {children}
    </motion.div>
  );
};
